/**
 * Configuration des templates de démarches administratives françaises
 * Sources officielles : service-public.fr, ANTS
 */

export type DemarcheCategory = 
  | 'identite' 
  | 'logement' 
  | 'vehicule' 
  | 'famille' 
  | 'banque' 
  | 'sante' 
  | 'pro'
  | 'fiscal';

export type StepType = 'task' | 'rdv' | 'payment' | 'online';

export interface DemarcheDocument {
  key: string;
  label: string;
  doc_type: string; // Matches document_type enum
  is_required: boolean;
  validity_rule?: string; // "< 3 mois", "< 6 mois", "< 1 an"
  description?: string;
}

export interface DemarcheStep {
  key: string;
  label: string;
  step_type: StepType;
  url?: string;
  description?: string;
}

export interface DemarcheTemplate {
  key: string;
  label: string;
  description: string;
  category: DemarcheCategory;
  icon: string; // Lucide icon name
  official_url?: string;
  estimated_days?: number;
  documents: DemarcheDocument[];
  steps: DemarcheStep[];
}

export const DEMARCHE_CATEGORIES: Record<DemarcheCategory, { label: string; icon: string; color: string }> = {
  identite: { label: 'Identité', icon: 'IdCard', color: 'bg-blue-500' },
  logement: { label: 'Logement', icon: 'Home', color: 'bg-green-500' },
  vehicule: { label: 'Véhicule', icon: 'Car', color: 'bg-orange-500' },
  famille: { label: 'Famille', icon: 'Users', color: 'bg-pink-500' },
  banque: { label: 'Banque & Finances', icon: 'Landmark', color: 'bg-purple-500' },
  sante: { label: 'Santé', icon: 'Heart', color: 'bg-red-500' },
  pro: { label: 'Professionnel', icon: 'Briefcase', color: 'bg-slate-500' },
  fiscal: { label: 'Fiscal', icon: 'Receipt', color: 'bg-amber-500' },
};

/**
 * Catalogue des 20 templates de démarches vérifiés
 * Sources : service-public.fr, ANTS
 */
export const DEMARCHES_CATALOG: DemarcheTemplate[] = [
  // ============ IDENTITÉ ============
  {
    key: 'passeport_mineur_premiere',
    label: 'Passeport enfant - 1ère demande',
    description: 'Première demande de passeport pour un mineur',
    category: 'identite',
    icon: 'Passport',
    official_url: 'https://www.service-public.fr/particuliers/vosdroits/F14930',
    estimated_days: 21,
    documents: [
      { key: 'photo', label: 'Photo d\'identité conforme', doc_type: 'photo', is_required: true, validity_rule: '< 6 mois', description: 'Format 35x45mm, fond clair, expression neutre' },
      { key: 'justif_domicile', label: 'Justificatif de domicile', doc_type: 'invoice', is_required: true, validity_rule: '< 1 an', description: 'Facture énergie, téléphone fixe, avis d\'imposition' },
      { key: 'cni_parent', label: 'CNI du parent (représentant légal)', doc_type: 'identity', is_required: true, description: 'CNI en cours de validité du parent qui accompagne' },
      { key: 'acte_naissance', label: 'Acte de naissance', doc_type: 'certificate', is_required: false, validity_rule: '< 3 mois', description: 'Requis si état civil non dématérialisé dans votre commune' },
      { key: 'livret_famille', label: 'Livret de famille', doc_type: 'certificate', is_required: false, description: 'Pages parents + enfant concerné' },
    ],
    steps: [
      { key: 'ants', label: 'Faire la pré-demande sur ANTS', step_type: 'online', url: 'https://passeport.ants.gouv.fr/', description: 'Récupérer le numéro de pré-demande' },
      { key: 'timbre', label: 'Acheter le timbre fiscal', step_type: 'payment', url: 'https://timbres.impots.gouv.fr/', description: '17€ (0-14 ans) ou 42€ (15-17 ans)' },
      { key: 'rdv_mairie', label: 'Prendre RDV en mairie', step_type: 'rdv', description: 'Mairie équipée d\'un dispositif de recueil' },
      { key: 'depot', label: 'Déposer le dossier en mairie', step_type: 'task', description: 'Avec l\'enfant obligatoirement présent' },
      { key: 'retrait', label: 'Retirer le passeport', step_type: 'task', description: 'L\'enfant doit être présent si 12 ans ou plus' },
    ],
  },
  {
    key: 'passeport_adulte_renouvellement',
    label: 'Passeport adulte - Renouvellement',
    description: 'Renouvellement de passeport pour majeur',
    category: 'identite',
    icon: 'Passport',
    official_url: 'https://www.service-public.fr/particuliers/vosdroits/F21091',
    estimated_days: 21,
    documents: [
      { key: 'photo', label: 'Photo d\'identité conforme', doc_type: 'photo', is_required: true, validity_rule: '< 6 mois' },
      { key: 'justif_domicile', label: 'Justificatif de domicile', doc_type: 'invoice', is_required: true, validity_rule: '< 1 an' },
      { key: 'ancien_passeport', label: 'Ancien passeport', doc_type: 'identity', is_required: true, description: 'Même périmé' },
    ],
    steps: [
      { key: 'ants', label: 'Faire la pré-demande sur ANTS', step_type: 'online', url: 'https://passeport.ants.gouv.fr/' },
      { key: 'timbre', label: 'Acheter le timbre fiscal (86€)', step_type: 'payment', url: 'https://timbres.impots.gouv.fr/' },
      { key: 'rdv_mairie', label: 'Prendre RDV en mairie', step_type: 'rdv' },
      { key: 'depot', label: 'Déposer le dossier en mairie', step_type: 'task' },
      { key: 'retrait', label: 'Retirer le passeport', step_type: 'task' },
    ],
  },
  {
    key: 'cni_mineur_premiere',
    label: 'Carte d\'identité enfant - 1ère demande',
    description: 'Première demande de CNI pour un mineur',
    category: 'identite',
    icon: 'IdCard',
    official_url: 'https://www.service-public.fr/particuliers/vosdroits/F1341',
    estimated_days: 21,
    documents: [
      { key: 'photo', label: 'Photo d\'identité conforme', doc_type: 'photo', is_required: true, validity_rule: '< 6 mois' },
      { key: 'justif_domicile', label: 'Justificatif de domicile', doc_type: 'invoice', is_required: true, validity_rule: '< 1 an' },
      { key: 'cni_parent', label: 'CNI du parent (représentant légal)', doc_type: 'identity', is_required: true },
      { key: 'acte_naissance', label: 'Acte de naissance', doc_type: 'certificate', is_required: false, validity_rule: '< 3 mois' },
    ],
    steps: [
      { key: 'ants', label: 'Faire la pré-demande sur ANTS', step_type: 'online', url: 'https://cni.ants.gouv.fr/' },
      { key: 'rdv_mairie', label: 'Prendre RDV en mairie', step_type: 'rdv' },
      { key: 'depot', label: 'Déposer le dossier avec l\'enfant', step_type: 'task' },
      { key: 'retrait', label: 'Retirer la CNI', step_type: 'task' },
    ],
  },
  {
    key: 'cni_adulte_renouvellement',
    label: 'Carte d\'identité adulte - Renouvellement',
    description: 'Renouvellement de CNI pour majeur',
    category: 'identite',
    icon: 'IdCard',
    official_url: 'https://www.service-public.fr/particuliers/vosdroits/F1342',
    estimated_days: 21,
    documents: [
      { key: 'photo', label: 'Photo d\'identité conforme', doc_type: 'photo', is_required: true, validity_rule: '< 6 mois' },
      { key: 'justif_domicile', label: 'Justificatif de domicile', doc_type: 'invoice', is_required: true, validity_rule: '< 1 an' },
      { key: 'ancienne_cni', label: 'Ancienne carte d\'identité', doc_type: 'identity', is_required: true },
    ],
    steps: [
      { key: 'ants', label: 'Faire la pré-demande sur ANTS', step_type: 'online', url: 'https://cni.ants.gouv.fr/' },
      { key: 'rdv_mairie', label: 'Prendre RDV en mairie', step_type: 'rdv' },
      { key: 'depot', label: 'Déposer le dossier en mairie', step_type: 'task' },
      { key: 'retrait', label: 'Retirer la CNI', step_type: 'task' },
    ],
  },

  // ============ LOGEMENT ============
  {
    key: 'dossier_location',
    label: 'Constituer un dossier de location',
    description: 'Rassembler les documents pour candidater à un appartement',
    category: 'logement',
    icon: 'Home',
    official_url: 'https://www.service-public.fr/particuliers/vosdroits/F1169',
    documents: [
      { key: 'cni', label: 'Pièce d\'identité en cours de validité', doc_type: 'identity', is_required: true, description: 'CNI, passeport, permis de conduire ou carte de séjour' },
      { key: 'justif_domicile', label: 'Justificatif de domicile actuel', doc_type: 'invoice', is_required: true, description: '3 dernières quittances de loyer ou attestation d\'hébergement' },
      { key: 'bulletins', label: '3 derniers bulletins de salaire', doc_type: 'payslip', is_required: true },
      { key: 'avis_imposition', label: 'Dernier avis d\'imposition', doc_type: 'tax', is_required: true },
      { key: 'contrat_travail', label: 'Contrat de travail ou attestation employeur', doc_type: 'contract', is_required: true, description: 'CDI, CDD, attestation Pôle Emploi, etc.' },
    ],
    steps: [
      { key: 'dossierfacile', label: 'Créer son dossier sur DossierFacile.fr', step_type: 'online', url: 'https://www.dossierfacile.fr/', description: 'Dossier certifié gratuit par l\'État' },
      { key: 'garant', label: 'Préparer les documents du garant', step_type: 'task', description: 'Mêmes documents que le locataire' },
      { key: 'visale', label: 'Demander la garantie Visale si éligible', step_type: 'online', url: 'https://www.visale.fr/', description: 'Garantie gratuite d\'Action Logement' },
    ],
  },
  {
    key: 'demenagement',
    label: 'Déménagement - Changement d\'adresse',
    description: 'Effectuer toutes les démarches liées à un déménagement',
    category: 'logement',
    icon: 'Truck',
    official_url: 'https://www.service-public.fr/particuliers/vosdroits/F33240',
    documents: [
      { key: 'nouveau_justif', label: 'Nouveau justificatif de domicile', doc_type: 'invoice', is_required: true },
      { key: 'attestation_assurance', label: 'Attestation d\'assurance habitation', doc_type: 'insurance', is_required: true },
      { key: 'etat_lieux_sortie', label: 'État des lieux de sortie', doc_type: 'other', is_required: false },
    ],
    steps: [
      { key: 'service_public', label: 'Déclarer le changement sur service-public.fr', step_type: 'online', url: 'https://www.service-public.fr/particuliers/vosdroits/F33240', description: 'Informe plusieurs organismes en une fois' },
      { key: 'la_poste', label: 'Faire suivre le courrier (La Poste)', step_type: 'online', url: 'https://www.laposte.fr/particulier/demenagement' },
      { key: 'edf', label: 'Transférer les contrats énergie', step_type: 'task' },
      { key: 'caf', label: 'Mettre à jour la CAF', step_type: 'online', url: 'https://www.caf.fr/' },
      { key: 'impots', label: 'Mettre à jour sur impots.gouv.fr', step_type: 'online', url: 'https://www.impots.gouv.fr/' },
    ],
  },

  // ============ VÉHICULE ============
  {
    key: 'carte_grise_changement_adresse',
    label: 'Changement d\'adresse carte grise',
    description: 'Mettre à jour l\'adresse sur le certificat d\'immatriculation',
    category: 'vehicule',
    icon: 'Car',
    official_url: 'https://www.service-public.fr/particuliers/vosdroits/F1050',
    estimated_days: 7,
    documents: [
      { key: 'carte_grise', label: 'Carte grise actuelle', doc_type: 'vehicle', is_required: true },
      { key: 'justif_domicile', label: 'Justificatif de nouveau domicile', doc_type: 'invoice', is_required: true, validity_rule: '< 6 mois' },
    ],
    steps: [
      { key: 'ants', label: 'Faire la demande sur ANTS', step_type: 'online', url: 'https://immatriculation.ants.gouv.fr/', description: 'Gratuit dans le mois suivant le déménagement' },
      { key: 'reception', label: 'Réceptionner l\'étiquette ou la nouvelle carte grise', step_type: 'task' },
    ],
  },
  {
    key: 'immatriculation_occasion',
    label: 'Immatriculation véhicule d\'occasion',
    description: 'Immatriculer un véhicule d\'occasion acheté à un particulier',
    category: 'vehicule',
    icon: 'Car',
    official_url: 'https://www.service-public.fr/particuliers/vosdroits/F1050',
    estimated_days: 14,
    documents: [
      { key: 'cni', label: 'Pièce d\'identité', doc_type: 'identity', is_required: true },
      { key: 'justif_domicile', label: 'Justificatif de domicile', doc_type: 'invoice', is_required: true, validity_rule: '< 6 mois' },
      { key: 'carte_grise_barree', label: 'Carte grise barrée par le vendeur', doc_type: 'vehicle', is_required: true, description: 'Avec mention "Vendu le..." et signature' },
      { key: 'certificat_cession', label: 'Certificat de cession (Cerfa 15776)', doc_type: 'other', is_required: true },
      { key: 'ct_valide', label: 'Contrôle technique valide', doc_type: 'vehicle', is_required: true, validity_rule: '< 6 mois', description: 'Véhicules de plus de 4 ans' },
      { key: 'permis', label: 'Permis de conduire', doc_type: 'driving_license', is_required: true },
      { key: 'assurance', label: 'Attestation d\'assurance', doc_type: 'insurance', is_required: true },
    ],
    steps: [
      { key: 'code_cession', label: 'Récupérer le code de cession du vendeur', step_type: 'task', description: 'Le vendeur doit le générer sur ANTS' },
      { key: 'ants', label: 'Faire la demande sur ANTS', step_type: 'online', url: 'https://immatriculation.ants.gouv.fr/' },
      { key: 'paiement', label: 'Payer les taxes (selon région)', step_type: 'payment' },
      { key: 'reception', label: 'Réceptionner la carte grise définitive', step_type: 'task' },
    ],
  },
  {
    key: 'controle_technique',
    label: 'Contrôle technique',
    description: 'Passer le contrôle technique périodique',
    category: 'vehicule',
    icon: 'ClipboardCheck',
    official_url: 'https://www.service-public.fr/particuliers/vosdroits/F2878',
    documents: [
      { key: 'carte_grise', label: 'Carte grise du véhicule', doc_type: 'vehicle', is_required: true },
      { key: 'ancien_ct', label: 'Ancien contrôle technique', doc_type: 'vehicle', is_required: false },
    ],
    steps: [
      { key: 'rdv', label: 'Prendre RDV dans un centre agréé', step_type: 'rdv' },
      { key: 'ct', label: 'Passer le contrôle technique', step_type: 'task' },
      { key: 'contre_visite', label: 'Contre-visite si nécessaire', step_type: 'task', description: 'Sous 2 mois en cas de défaillances majeures' },
    ],
  },

  // ============ FAMILLE / SCOLARITÉ ============
  {
    key: 'inscription_ecole',
    label: 'Inscription école maternelle/primaire',
    description: 'Inscrire un enfant à l\'école publique',
    category: 'famille',
    icon: 'GraduationCap',
    official_url: 'https://www.service-public.fr/particuliers/vosdroits/F1864',
    documents: [
      { key: 'livret_famille', label: 'Livret de famille ou acte de naissance', doc_type: 'certificate', is_required: true },
      { key: 'justif_domicile', label: 'Justificatif de domicile récent', doc_type: 'invoice', is_required: true },
      { key: 'carnet_vaccins', label: 'Carnet de vaccinations', doc_type: 'medical', is_required: true, description: 'Vaccins obligatoires à jour' },
      { key: 'cni_parent', label: 'Pièce d\'identité du parent', doc_type: 'identity', is_required: false },
    ],
    steps: [
      { key: 'mairie', label: 'Inscription en mairie', step_type: 'task', description: 'Obtenir le certificat d\'inscription' },
      { key: 'ecole', label: 'Inscription à l\'école', step_type: 'task', description: 'Avec le certificat d\'inscription de la mairie' },
    ],
  },
  {
    key: 'inscription_cantine',
    label: 'Inscription cantine / périscolaire',
    description: 'Inscrire un enfant à la cantine et aux activités périscolaires',
    category: 'famille',
    icon: 'Utensils',
    documents: [
      { key: 'livret_famille', label: 'Livret de famille', doc_type: 'certificate', is_required: true },
      { key: 'justif_domicile', label: 'Justificatif de domicile', doc_type: 'invoice', is_required: true },
      { key: 'avis_imposition', label: 'Dernier avis d\'imposition', doc_type: 'tax', is_required: true, description: 'Pour calculer le tarif selon le quotient familial' },
      { key: 'attestation_employeur', label: 'Attestation employeur (horaires)', doc_type: 'other', is_required: false },
      { key: 'certificat_allergie', label: 'Certificat médical si allergie', doc_type: 'medical', is_required: false },
    ],
    steps: [
      { key: 'inscription', label: 'Inscription auprès de la mairie ou de l\'école', step_type: 'task' },
      { key: 'pai', label: 'Établir un PAI si allergie alimentaire', step_type: 'task', description: 'Projet d\'Accueil Individualisé' },
    ],
  },
  {
    key: 'caf_prime_naissance',
    label: 'CAF - Prime à la naissance',
    description: 'Demander la prime à la naissance / adoption',
    category: 'famille',
    icon: 'Baby',
    official_url: 'https://www.service-public.fr/particuliers/vosdroits/F2550',
    documents: [
      { key: 'livret_famille', label: 'Livret de famille mis à jour', doc_type: 'certificate', is_required: true },
      { key: 'rib', label: 'RIB', doc_type: 'bank', is_required: true },
      { key: 'justif_domicile', label: 'Justificatif de domicile', doc_type: 'invoice', is_required: true },
      { key: 'avis_imposition', label: 'Avis d\'imposition', doc_type: 'tax', is_required: true },
      { key: 'certificat_grossesse', label: 'Certificat de grossesse', doc_type: 'medical', is_required: true, description: 'Déclaré avant la fin du 3ème mois' },
    ],
    steps: [
      { key: 'declaration', label: 'Déclarer la grossesse à la CAF', step_type: 'online', url: 'https://www.caf.fr/', description: 'Dans les 14 premières semaines' },
      { key: 'suivi', label: 'Effectuer les examens prénataux', step_type: 'task' },
    ],
  },

  // ============ BANQUE / FINANCES ============
  {
    key: 'credit_immobilier',
    label: 'Demande de crédit immobilier',
    description: 'Constituer un dossier de prêt immobilier',
    category: 'banque',
    icon: 'Landmark',
    documents: [
      { key: 'cni', label: 'Pièce d\'identité', doc_type: 'identity', is_required: true },
      { key: 'bulletins', label: '3 derniers bulletins de salaire', doc_type: 'payslip', is_required: true },
      { key: 'avis_imposition', label: 'Avis d\'imposition N-1 et N-2', doc_type: 'tax', is_required: true },
      { key: 'contrat_travail', label: 'Contrat de travail', doc_type: 'contract', is_required: true },
      { key: 'rib', label: 'RIB', doc_type: 'bank', is_required: true },
      { key: 'releves_bancaires', label: '3 derniers relevés de compte', doc_type: 'bank', is_required: true },
      { key: 'compromis', label: 'Compromis de vente', doc_type: 'contract', is_required: true, description: 'Ou promesse de vente' },
      { key: 'justif_apport', label: 'Justificatifs d\'apport personnel', doc_type: 'bank', is_required: false },
    ],
    steps: [
      { key: 'simulation', label: 'Faire une simulation de prêt', step_type: 'online' },
      { key: 'rdv_banque', label: 'Prendre RDV avec un conseiller bancaire', step_type: 'rdv' },
      { key: 'comparaison', label: 'Comparer les offres de plusieurs banques', step_type: 'task' },
      { key: 'offre', label: 'Accepter l\'offre de prêt (après 10 jours)', step_type: 'task' },
    ],
  },
  {
    key: 'ouverture_compte',
    label: 'Ouverture de compte bancaire',
    description: 'Ouvrir un compte courant dans une banque',
    category: 'banque',
    icon: 'CreditCard',
    documents: [
      { key: 'cni', label: 'Pièce d\'identité', doc_type: 'identity', is_required: true },
      { key: 'justif_domicile', label: 'Justificatif de domicile', doc_type: 'invoice', is_required: true, validity_rule: '< 3 mois' },
      { key: 'specimen_signature', label: 'Spécimen de signature', doc_type: 'other', is_required: false },
    ],
    steps: [
      { key: 'comparaison', label: 'Comparer les offres bancaires', step_type: 'task' },
      { key: 'rdv', label: 'Prendre RDV en agence ou en ligne', step_type: 'rdv' },
      { key: 'ouverture', label: 'Signer la convention de compte', step_type: 'task' },
    ],
  },

  // ============ SANTÉ ============
  {
    key: 'carte_vitale_maj',
    label: 'Mise à jour carte Vitale',
    description: 'Mettre à jour sa carte Vitale après changement de situation',
    category: 'sante',
    icon: 'Heart',
    official_url: 'https://www.ameli.fr/assure/remboursements/etre-bien-rembourse/carte-vitale',
    documents: [
      { key: 'carte_vitale', label: 'Carte Vitale actuelle', doc_type: 'medical', is_required: true },
      { key: 'photo', label: 'Photo d\'identité', doc_type: 'photo', is_required: false, description: 'Si renouvellement avec photo' },
    ],
    steps: [
      { key: 'borne', label: 'Se rendre à une borne de mise à jour', step_type: 'task', description: 'Pharmacie, CPAM, hôpital' },
      { key: 'ou_ameli', label: 'Ou demander via ameli.fr', step_type: 'online', url: 'https://www.ameli.fr/' },
    ],
  },
  {
    key: 'affiliation_mutuelle',
    label: 'Affiliation à une mutuelle santé',
    description: 'Souscrire à une complémentaire santé',
    category: 'sante',
    icon: 'Shield',
    documents: [
      { key: 'cni', label: 'Pièce d\'identité', doc_type: 'identity', is_required: true },
      { key: 'rib', label: 'RIB', doc_type: 'bank', is_required: true },
      { key: 'attestation_vitale', label: 'Attestation de droits Ameli', doc_type: 'medical', is_required: true },
      { key: 'numero_secu', label: 'Numéro de sécurité sociale', doc_type: 'other', is_required: true },
    ],
    steps: [
      { key: 'comparaison', label: 'Comparer les offres de mutuelles', step_type: 'task' },
      { key: 'souscription', label: 'Souscrire au contrat', step_type: 'task' },
      { key: 'carte_tp', label: 'Réceptionner la carte de tiers payant', step_type: 'task' },
    ],
  },

  // ============ FISCAL ============
  {
    key: 'declaration_impots',
    label: 'Déclaration d\'impôts',
    description: 'Effectuer sa déclaration annuelle de revenus',
    category: 'fiscal',
    icon: 'Receipt',
    official_url: 'https://www.impots.gouv.fr/',
    documents: [
      { key: 'avis_imposition_precedent', label: 'Avis d\'imposition N-1', doc_type: 'tax', is_required: false },
      { key: 'bulletins_decembre', label: 'Bulletin de décembre', doc_type: 'payslip', is_required: true, description: 'Récapitulatif annuel des revenus' },
      { key: 'attestation_interets', label: 'Attestation d\'intérêts d\'emprunt', doc_type: 'bank', is_required: false },
      { key: 'justif_dons', label: 'Reçus fiscaux des dons', doc_type: 'other', is_required: false },
      { key: 'justif_garde', label: 'Attestation frais de garde', doc_type: 'other', is_required: false },
    ],
    steps: [
      { key: 'connexion', label: 'Se connecter sur impots.gouv.fr', step_type: 'online', url: 'https://www.impots.gouv.fr/' },
      { key: 'verification', label: 'Vérifier la déclaration pré-remplie', step_type: 'task' },
      { key: 'ajouts', label: 'Ajouter les réductions/crédits d\'impôt', step_type: 'task' },
      { key: 'signature', label: 'Signer électroniquement', step_type: 'task' },
    ],
  },

  // ============ PROFESSIONNEL ============
  {
    key: 'creation_micro_entreprise',
    label: 'Création de micro-entreprise',
    description: 'S\'immatriculer comme auto-entrepreneur',
    category: 'pro',
    icon: 'Briefcase',
    official_url: 'https://www.autoentrepreneur.urssaf.fr/',
    documents: [
      { key: 'cni', label: 'Pièce d\'identité', doc_type: 'identity', is_required: true },
      { key: 'justif_domicile', label: 'Justificatif de domicile', doc_type: 'invoice', is_required: true },
      { key: 'rib', label: 'RIB professionnel', doc_type: 'bank', is_required: true },
    ],
    steps: [
      { key: 'guichet', label: 'S\'inscrire sur le guichet unique', step_type: 'online', url: 'https://procedures.inpi.fr/' },
      { key: 'acre', label: 'Demander l\'ACRE si éligible', step_type: 'task', description: 'Aide aux créateurs d\'entreprise' },
      { key: 'compte_urssaf', label: 'Créer son compte URSSAF', step_type: 'online', url: 'https://www.autoentrepreneur.urssaf.fr/' },
    ],
  },
  {
    key: 'titre_sejour_renouvellement',
    label: 'Renouvellement titre de séjour',
    description: 'Renouveler son titre de séjour en France',
    category: 'identite',
    icon: 'Passport',
    official_url: 'https://www.service-public.fr/particuliers/vosdroits/N110',
    estimated_days: 90,
    documents: [
      { key: 'passeport', label: 'Passeport en cours de validité', doc_type: 'identity', is_required: true },
      { key: 'ancien_titre', label: 'Titre de séjour actuel', doc_type: 'identity', is_required: true },
      { key: 'photos', label: '3 photos d\'identité', doc_type: 'photo', is_required: true },
      { key: 'justif_domicile', label: 'Justificatif de domicile', doc_type: 'invoice', is_required: true, validity_rule: '< 6 mois' },
      { key: 'justif_ressources', label: 'Justificatifs de ressources', doc_type: 'payslip', is_required: true },
      { key: 'contrat_travail', label: 'Contrat de travail', doc_type: 'contract', is_required: false },
    ],
    steps: [
      { key: 'rdv', label: 'Prendre RDV en préfecture', step_type: 'rdv', description: '2 mois avant expiration' },
      { key: 'depot', label: 'Déposer le dossier en préfecture', step_type: 'task' },
      { key: 'taxes', label: 'Payer les taxes (timbre fiscal)', step_type: 'payment' },
      { key: 'retrait', label: 'Retirer le nouveau titre', step_type: 'task' },
    ],
  },
];

/**
 * Récupère un template par sa clé
 */
export function getDemarcheTemplate(key: string): DemarcheTemplate | undefined {
  return DEMARCHES_CATALOG.find(t => t.key === key);
}

/**
 * Récupère les templates par catégorie
 */
export function getDemarchesByCategory(category: DemarcheCategory): DemarcheTemplate[] {
  return DEMARCHES_CATALOG.filter(t => t.category === category);
}

/**
 * Recherche dans le catalogue
 */
export function searchDemarches(query: string): DemarcheTemplate[] {
  const q = query.toLowerCase();
  return DEMARCHES_CATALOG.filter(t => 
    t.label.toLowerCase().includes(q) || 
    t.description.toLowerCase().includes(q) ||
    t.documents.some(d => d.label.toLowerCase().includes(q))
  );
}
